import { Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { ReactComponent as LockUploadIcon } from 'assets/icons/lock_download.svg';
import { ALLOW_IMAGE_SIZE_MB, ALLOW_IMAGE_TYPES } from 'constants/imageUpload';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { UPGRADE } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectIsUploading } from 'modules/Editor/store/editorSettingsSelector';
import { UPLOAD_IMAGES_LIMITS, useUser } from 'modules/User';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

interface DragDropAreaProps {
  isOnDrag: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUploadBtnClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const DragDropArea = ({
  isOnDrag,
  inputRef,
  handleFileChange,
  handleUploadBtnClick,
}: DragDropAreaProps): ReactElement => {
  const ACCEPTED_IMAGE_TYPES_STR = ALLOW_IMAGE_TYPES.join(',');
  const isUploading = useAppSelector(selectIsUploading);
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu' });

  return (
    <>
      <Text
        marginY='2'
        fontSize='sm'
        fontWeight='semibold'
        lineHeight='1.25'
        color={isOnDrag ? 'upgrade.blue.700' : 'font.500'}
      >
        {t('dragAndDrop')}
      </Text>
      <Text fontSize='xs' color='font.500' lineHeight='1.33' textAlign='center' borderColor={'font.500'}>
        {t('formats')}
      </Text>
      <Text fontSize='xs' color='font.500' lineHeight='1.33' textAlign='center' borderColor={'font.500'}>
        {t('maxAllowedSize', { size: ALLOW_IMAGE_SIZE_MB })}
      </Text>
      <Input
        id='image-upload'
        type='file'
        ref={inputRef}
        display='none'
        onChange={handleFileChange}
        accept={ACCEPTED_IMAGE_TYPES_STR}
        aria-label={t('uploadInputAriaLabel')}
        multiple
      />
      <Button
        size='sm'
        h='8'
        py={2.5}
        px={2}
        fontWeight={'medium'}
        minW='7.75rem'
        marginTop='4'
        variant='action'
        onClick={handleUploadBtnClick}
        isDisabled={isUploading}
      >
        {isUploading ? (
          <Flex flexDir='row' justifyContent='center' alignItems='center'>
            <Spinner speed='0.65s' size='sm' />
            <Text ml='2'>{t('uploading')}</Text>
          </Flex>
        ) : (
          t('browseFiles')
        )}
      </Button>
    </>
  );
};

type HandleClickUpgrade = (event: React.MouseEvent<HTMLButtonElement>) => void;

export const GatedDragDropArea = (): ReactElement => {
  const { data: userInfo } = useUser();
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu' });
  const handleClickUpgrade: HandleClickUpgrade = () => {
    const userType = userInfo?.user_type;
    const gatedImageNum = userType ? UPLOAD_IMAGES_LIMITS[userType] : 0;
    const mpFeatureProp = `${UPGRADE.feature.ImageUploadLimit} ${gatedImageNum}`;
    // Set mixpanel_feature session storage item used by `Analytics: Upgrade` event
    setAnalyticsUpgradeFeatureProp(mpFeatureProp);
    window.open(UPGRADE_REDIRECT_LINK, '_self');

    Mixpanel.track(UPGRADE.eventName, { Feature: mpFeatureProp });
  };

  return (
    <>
      <Text
        marginY='3'
        w='15rem'
        fontSize='sm'
        fontWeight='medium'
        lineHeight='1.43'
        color='font.500'
        textAlign='center'
      >
        {t('reachedMaxUploads')}
      </Text>
      <Button
        h='8'
        marginY='1'
        variant='upgrade'
        onClick={handleClickUpgrade}
        leftIcon={<LockUploadIcon stroke='white' />}
      >
        {t('upgrade')}
      </Button>
    </>
  );
};
